export type MultilingualLocale = "en" | "th" | "id" | "vn" | "fr" | "ru" | "pt" | "es"

const SUPPORTED_LOCALES: readonly MultilingualLocale[] = ["en", "th", "id", "vn", "fr", "ru", "pt", "es"]

const PATH_PREFIX_BY_LOCALE: Record<MultilingualLocale, string> = {
  en: "",
  th: "/th",
  id: "/id",
  vn: "/vn",
  fr: "/fr",
  ru: "/ru",
  pt: "/pt",
  es: "/es",
}

const HREFLANG_BY_LOCALE: Record<MultilingualLocale, string> = {
  en: "en",
  th: "th",
  id: "id",
  vn: "vi-VN",
  fr: "fr",
  ru: "ru",
  pt: "pt",
  es: "es",
}

export interface AlternateLink {
  locale: MultilingualLocale
  hreflang: string
  href: string
}

export interface MultilingualSeoLinks {
  canonical: string
  alternates: AlternateLink[]
  xDefault: string
  currentLocale: MultilingualLocale
}

export interface BuildMultilingualSeoLinksInput {
  pathname: string
  host?: string | null
  protocol?: string | null
  currentLocale?: string
  defaultLocale?: MultilingualLocale
}

function normalizePathname(pathname: string): string {
  const withoutQuery = pathname.split("?")[0] || "/"
  const withoutHash = withoutQuery.split("#")[0] || "/"

  let normalized = withoutHash.startsWith("/") ? withoutHash : `/${withoutHash}`
  normalized = normalized.replace(/\/{2,}/g, "/")

  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1)
  }

  return normalized || "/"
}

function isSupportedLocale(value?: string): value is MultilingualLocale {
  if (!value) {
    return false
  }

  return SUPPORTED_LOCALES.includes(value as MultilingualLocale)
}

function getLocaleFromPath(pathname: string): MultilingualLocale | null {
  const normalizedPathname = normalizePathname(pathname)
  const [maybeLocale] = normalizedPathname.split("/").filter(Boolean)

  if (!isSupportedLocale(maybeLocale)) {
    return null
  }

  return maybeLocale
}

function getPathSuffixWithoutLocale(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname)
  const segments = normalizedPathname.split("/").filter(Boolean)

  if (segments.length === 0) {
    return ""
  }

  const firstSegment = segments[0]
  const restSegments = isSupportedLocale(firstSegment) ? segments.slice(1) : segments

  return restSegments.length > 0 ? `/${restSegments.join("/")}` : ""
}

function buildLocalePath(locale: MultilingualLocale, suffix: string): string {
  const prefix = PATH_PREFIX_BY_LOCALE[locale]

  if (!suffix) {
    return prefix ? `${prefix}/` : "/"
  }

  return `${prefix}${suffix}`
}

function buildOrigin(host?: string | null, protocol?: string | null): string {
  const safeHost = (host || "www.metal.com").split(",")[0]?.trim() || "www.metal.com"
  const safeProtocol = (protocol || "https").split(",")[0]?.trim() || "https"

  return `${safeProtocol}://${safeHost}`
}

export function buildMultilingualSeoLinks(input: BuildMultilingualSeoLinksInput): MultilingualSeoLinks {
  const normalizedPathname = normalizePathname(input.pathname || "/")
  const localeFromPath = getLocaleFromPath(normalizedPathname)
  const defaultLocale = isSupportedLocale(input.defaultLocale) ? input.defaultLocale : "en"
  const currentLocale = isSupportedLocale(input.currentLocale) ? input.currentLocale : localeFromPath || defaultLocale
  const suffix = getPathSuffixWithoutLocale(normalizedPathname)
  const origin = buildOrigin(input.host, input.protocol)

  const alternates = SUPPORTED_LOCALES.map((locale) => ({
    locale,
    hreflang: HREFLANG_BY_LOCALE[locale],
    href: `${origin}${buildLocalePath(locale, suffix)}`,
  }))

  return {
    canonical: `${origin}${buildLocalePath(currentLocale, suffix)}`,
    alternates,
    xDefault: `${origin}${buildLocalePath("en", suffix)}`,
    currentLocale,
  }
}
